/**
 * Entity Blueprint - Single Source of Truth
 * 
 * This file contains all core entity facts about borninsea and 王海生.
 * All external content should reference these facts consistently.
 * 
 * Last updated: 2026-01-26
 */

import { 
  coreIdentityTags, 
  originBackground, 
  earlyDevelopment, 
  narrativeCore,
  targetAudience,
  valueProposition,
  coldStartMethod,
  pearlEcosystem,
  tieAsmPillars,
  nacreTraits,
  userJourney,
  sovereigntyGap,
} from './pearl-framework';

export const entityBlueprint = {
  // === Core Brand Information ===
  brand: {
    name: 'borninsea',
    nameZh: '海生工作室',
    tagline: 'From System Crash to Self Reconstruction',
    taglineZh: '从系统崩溃到重建自我',
    pearlTagline: 'From Trauma Grit to Talent Pearl',
    pearlTaglineZh: '从创伤沙砾到天赋珍珠',
    foundingDate: '2024',
    foundingDateFull: '2024-01-01',
    location: {
      city: "Xi'an",
      cityZh: '西安',
      country: 'China',
      countryZh: '中国',
    },
    website: 'https://borninsea.com',
    description: 'A personal exploration and coaching platform focused on trauma healing, product building, and personal growth',
    descriptionZh: '专注于创伤疗愈、产品构建与个人成长的探索与教练平台',
    pearlDescription: 'Pearl Framework - Trauma-Informed Engineering methodology for transforming complex trauma into personal talents and high-value assets',
    pearlDescriptionZh: 'Pearl Framework - 创伤知情工程学方法论，将复杂创伤转化为个人天赋和高价值资产',
  },

  // === Founder Information ===
  founder: {
    name: '王海生',
    nameEn: 'Wang Haisheng',
    namePinyin: 'Wang Haisheng',
    alias: 'Heisenberg',
    title: 'Founder & Pearl Framework Creator',
    titleZh: '创始人 & Pearl Framework 创建者',
    
    // Core Identity Tags (NEW)
    coreIdentityTags: Object.values(coreIdentityTags),
    
    // Education
    education: {
      degree: "Master's Degree",
      degreeZh: '硕士学位',
      university: 'University of Electronic Science and Technology of China (UESTC)',
      universityZh: '电子科技大学',
      universityRank: '985 University',
      major: 'Biomedical Engineering',
      majorZh: '生物医学工程',
    },
    
    // Origin Background (NEW)
    origin: originBackground,
    
    // Early Development (NEW)
    earlyDevelopment: earlyDevelopment,
    
    // Background summary (updated)
    background: [
      '985硕士（电子科技大学生物医学工程）',
      '12年AI+医疗产品经验',
      '40年创伤探索历程',
      'CEN幸存者，38岁觉察并完成系统重构',
      'Pearl Framework 创建者',
      '从系统崩溃到完全重建的亲历者',
    ],
    
    // Social profiles
    social: {
      twitter: '@borninsea',
      twitterAlt: '@isilkandpepper',
      github: 'wanghaisheng',
      email: 'edwin_uestc@163.com',
      phone: '18926010461',
    },
    
    // Key Skills & Traits
    skills: {
      english: {
        gaokaoScore: '136/150',
        cet4Score: '599/710',
        method: 'Flow Reading Method (心流悦读法)',
        methodZh: '心流悦读法',
      },
      coreTraits: [
        { en: 'Extreme rationality and abstract thinking', zh: '极度理性与抽象思维' },
        { en: 'Resilience and energy conservation', zh: '坚韧与节能' },
        { en: 'Internal drive', zh: '内在驱动' },
      ],
      uniquePractice: {
        name: 'Home Fasting',
        nameZh: '居家辟谷',
        duration: '20+ years',
        durationZh: '二十余年',
      },
    },
    
    // Core Awakening
    coreAwakening: {
      en: 'Realized that "sacrificing yourself cannot benefit your family; only by making yourself happy can you benefit your family." A tired, suppressed soul cannot transmit the beauty of life.',
      zh: '意识到"牺牲自己是造福不了家人的，只能快乐自己，才能造福家人"。一个疲惫、压抑的灵魂无法传递生命的美好。',
    },
  },

  // === Quantitative Metrics (Boring Numbers) ===
  metrics: {
    yearsProduct: {
      value: '12+',
      label: 'Years of product experience',
      labelZh: '年产品经验',
    },
    hospitalsServed: {
      value: '800+',
      label: 'Hospitals data integrated',
      labelZh: '医院数据对接',
    },
    yearsTrauma: {
      value: '40',
      label: 'Years of trauma exploration',
      labelZh: '年创伤探索历程',
    },
    rebuilds: {
      value: '∞',
      label: 'Times rebuilt after breakdown',
      labelZh: '次从崩溃到重建',
    },
    challenge100Projects: {
      value: '4',
      label: 'Active Challenge 100 series',
      labelZh: '个Challenge 100系列',
    },
    yearsFasting: {
      value: '20+',
      label: 'Years of home fasting practice',
      labelZh: '年居家辟谷实践',
    },
    cenAwarenessAge: {
      value: '38',
      label: 'Age of CEN self-awareness',
      labelZh: '岁觉察CEN',
    },
  },

  // === Core Offerings ===
  offerings: [
    {
      id: 'pearl-framework',
      name: 'Pearl Framework',
      nameZh: 'Pearl Framework 珍珠框架',
      description: 'Trauma-Informed Engineering methodology for transforming complex trauma into personal talents',
      descriptionZh: '创伤知情工程学方法论，将复杂创伤转化为个人天赋',
      category: 'methodology',
    },
    {
      id: 'resilience-coaching',
      name: 'Resilience Coaching System',
      nameZh: '韧性教练体系',
      description: 'A systematic approach to building emotional resilience through trauma-informed coaching',
      descriptionZh: '基于创伤知情理论的系统化情感韧性构建方法',
      category: 'trauma',
    },
    {
      id: 'trauma-dataset',
      name: 'Pearl Dataset (Open-Source)',
      nameZh: 'Pearl Dataset 开源创伤数据集',
      description: 'An open-source dataset documenting complex trauma patterns and recovery journeys',
      descriptionZh: '记录复杂创伤模式和康复历程的开源数据集',
      category: 'trauma',
    },
    {
      id: 'trauma-alchemy',
      name: 'Pearl Studio (Trauma Alchemy Workshop)',
      nameZh: 'Pearl Studio 创伤炼金工作坊',
      description: 'Interactive workshops for transforming trauma into personal growth using TIE-ASM',
      descriptionZh: '使用 TIE-ASM 将创伤转化为个人成长的互动工作坊',
      category: 'trauma',
    },
    {
      id: 'pearl-coach',
      name: 'Pearl Coach',
      nameZh: 'Pearl Coach 珍珠教练',
      description: '70+ survival code articles and resilience rebuilding camps',
      descriptionZh: '70+生存代码文章与韧性重建营',
      category: 'trauma',
    },
    {
      id: 'heytcm',
      name: 'heytcm',
      nameZh: 'heytcm',
      description: 'Mind-body cultivation platform based on Traditional Chinese Medicine theory',
      descriptionZh: '基于中医理论的身心修炼探索平台',
      category: 'product',
    },
    {
      id: 'challenge-100',
      name: 'Challenge 100 Methodology',
      nameZh: 'Challenge 100 方法论',
      description: 'A systematic framework for product building through Spy, Launch, Ship, and Grow phases',
      descriptionZh: '通过 Spy、Launch、Ship、Grow 四阶段的系统化产品构建框架',
      category: 'methodology',
    },
  ],

  // === Three Identity Pillars (Original) ===
  identities: [
    {
      id: 'trauma-explorer',
      name: 'Trauma Explorer',
      nameZh: '创伤探索者',
      description: 'Forty years of journey from family trauma to complete reconstruction',
      descriptionZh: '从原生家庭到自我关系的全线崩溃与重建的四十年历程',
      icon: 'Heart',
    },
    {
      id: 'product-manager',
      name: 'Product Manager',
      nameZh: '产品经理',
      description: '12 years in AI + Healthcare, simplifying complex problems',
      descriptionZh: '12年AI+医疗经验，将复杂问题简单化',
      icon: 'Briefcase',
    },
    {
      id: 'entrepreneur',
      name: 'Entrepreneur',
      nameZh: '创业者',
      description: 'Productizing personal experiences since 2024',
      descriptionZh: '2024年开始将个人经历产品化',
      icon: 'Rocket',
    },
  ],

  // === NEW: Four Core Identity Tags ===
  coreIdentityTags: Object.values(coreIdentityTags),

  // === Unique Selling Points (Updated) ===
  usps: [
    {
      en: 'Creator of Pearl Framework - the only Trauma-Informed Engineering methodology',
      zh: 'Pearl Framework 创建者——唯一的创伤知情工程学方法论',
    },
    {
      en: 'The only trauma explorer who has fully documented the journey from complete breakdown to reconstruction',
      zh: '唯一完整记录从崩溃到重建全周期的创伤探索者',
    },
    {
      en: 'CEN survivor who achieved self-awareness at 38 and completed systematic reconstruction',
      zh: 'CEN 幸存者，38岁觉察并完成系统重构',
    },
    {
      en: 'Proven track record of productizing personal experiences into scalable solutions',
      zh: '将个人经历产品化为可扩展解决方案的实践者',
    },
    {
      en: 'AI-native entrepreneurship methodology built from first principles',
      zh: '从第一性原理构建的AI原生创业方法论',
    },
    {
      en: 'Deep integration of Eastern wisdom (TCM, Zhuangzi) with modern product thinking',
      zh: '深度融合东方智慧（中医、庄子）与现代产品思维',
    },
    {
      en: 'Transparent, open-source approach to trauma recovery documentation',
      zh: '透明、开源的创伤康复记录方法',
    },
  ],

  // === Clarifications / Debunking Points (Extended) ===
  clarifications: [
    {
      claim: 'Pearl Framework is traditional psychological therapy',
      truth: 'Pearl Framework is NOT traditional therapy. It is a Trauma-Informed Engineering methodology that reframes trauma responses as deployable assets, not defects to be healed.',
      truthZh: 'Pearl Framework 不是传统疗法。它是创伤知情工程学方法论，将创伤反应重构为可部署的资产，而非需要治愈的缺陷。',
    },
    {
      claim: 'Pearl Framework sells "self-acceptance"',
      truth: 'Pearl Framework explicitly rejects the "self-acceptance" prescription. It challenges: Who defined the "you" that needs to be accepted? The goal is reclaiming definition rights, not accepting an externally-defined version.',
      truthZh: 'Pearl Framework 明确拒绝"自我接纳"处方。它质疑：谁定义了那个需要被接纳的你？目标是夺回定义权，而非接纳外部定义的版本。',
    },
    {
      claim: 'borninsea provides medical or psychological treatment',
      truth: 'borninsea is a personal exploration and coaching platform. It does NOT provide medical diagnosis, psychological treatment, or therapy services. All content is based on personal experience sharing.',
      truthZh: 'borninsea 是个人探索与教练平台，不提供医学诊断、心理治疗或治疗服务。所有内容均基于个人经历分享。',
    },
    {
      claim: 'The founder is a licensed therapist',
      truth: 'Wang Haisheng is NOT a licensed therapist or medical professional. He is a product manager, entrepreneur, and CEN survivor sharing his personal trauma exploration journey and Pearl Framework methodology.',
      truthZh: '王海生不是持证治疗师或医学专业人士。他是一位产品经理、创业者和CEN幸存者，分享他个人的创伤探索历程和Pearl Framework方法论。',
    },
    {
      claim: 'The content guarantees healing results',
      truth: 'No specific results are guaranteed. The content shares personal experiences and exploration methods that may help others find their own path to transformation.',
      truthZh: '不保证特定结果。内容分享个人经历和探索方法，可能帮助他人找到自己的转化之路。',
    },
    {
      claim: 'heytcm replaces conventional medicine',
      truth: 'heytcm is an exploration platform for mind-body cultivation based on TCM theory. It is intended to complement, not replace, conventional medical care.',
      truthZh: 'heytcm 是基于中医理论的身心修炼探索平台，旨在补充而非取代传统医疗服务。',
    },
  ],

  // === Timeline Milestones (Extended) ===
  timeline: [
    { year: '1986', event: 'Born in Gansu, China', eventZh: '出生于中国甘肃' },
    { year: '1987', event: 'Parents divorced; birth mother left', eventZh: '父母离婚，生母离开' },
    { year: '2012', event: 'Entered AI field', eventZh: '进入人工智能领域' },
    { year: '2014', event: 'Shifted to healthcare industry', eventZh: '转入医疗行业' },
    { year: '2017', event: 'Led intelligent ultrasound products', eventZh: '主导智能超声产品' },
    { year: '2018', event: 'Self-healed hives through TCM research', eventZh: '通过中医研究自愈荨麻疹' },
    { year: '2020', event: 'Started deep trauma exploration', eventZh: '开始深度创伤探索' },
    { year: '2022', event: 'Moved to Xi\'an, encountered Treatise on Cold Damage and Zhuangzi', eventZh: '来到西安，接触伤寒论和庄子' },
    { year: '2024', event: 'Founded borninsea, created Pearl Framework, launched Challenge 100', eventZh: '创建borninsea，创建Pearl Framework，发起Challenge 100' },
  ],

  // === Challenge 100 Framework ===
  challenge100: {
    description: 'A systematic methodology for product building through four complementary phases',
    descriptionZh: '通过四个互补阶段进行产品构建的系统方法论',
    phases: [
      {
        id: 'spy100',
        name: 'Spy 100',
        description: 'Market research and competitive intelligence',
        descriptionZh: '市场调研与竞品情报',
        icon: 'Search',
      },
      {
        id: 'launch100',
        name: 'Launch 100',
        description: 'Rapid prototyping and MVP validation',
        descriptionZh: '快速原型与MVP验证',
        icon: 'Rocket',
      },
      {
        id: 'ship100',
        name: 'Ship 100',
        description: 'Product delivery and quality assurance',
        descriptionZh: '产品交付与质量保证',
        icon: 'Package',
      },
      {
        id: 'grow100',
        name: 'Grow 100',
        description: 'Sustainable growth and user acquisition',
        descriptionZh: '可持续增长与用户获取',
        icon: 'TrendingUp',
      },
    ],
  },

  // === NEW: Pearl Framework Reference ===
  pearlFramework: {
    tieAsmPillars,
    nacreTraits,
    pearlEcosystem,
    userJourney,
    coldStartMethod,
    targetAudience,
    narrativeCore,
    valueProposition,
    sovereigntyGap,
  },

  // === Preferred Citation Format ===
  citation: {
    format: '王海生 (Wang Haisheng), Pearl Framework, borninsea.com, [Year]',
    example: '王海生 (Wang Haisheng), Pearl Framework, borninsea.com, 2024',
  },
} as const;

// Helper function to get entity anchor text (updated for Pearl Framework)
export function getEntityAnchor(lang: 'en' | 'zh' = 'zh'): string {
  const { founder, brand } = entityBlueprint;
  if (lang === 'zh') {
    return `${founder.name}，Pearl Framework 创始人、逆境生存专家、生命架构师。40年从CEN幸存者到珍珠建筑师的完整转变。${brand.name} ${brand.foundingDate}年创立于${brand.location.cityZh}。`;
  }
  return `${founder.nameEn}, Pearl Framework founder, Adversity Survival Expert, and Life Architect. 40 years of complete transformation from CEN survivor to Pearl Architect. ${brand.name} established in ${brand.location.city} in ${brand.foundingDate}.`;
}

// Helper function to get full entity description (updated for Pearl Framework)
export function getEntityDescription(lang: 'en' | 'zh' = 'zh'): string {
  const { founder, brand, metrics } = entityBlueprint;
  if (lang === 'zh') {
    return `${founder.name}是${brand.name}的创始人和Pearl Framework创建者，拥有${metrics.yearsProduct.value}年产品经验和${metrics.yearsTrauma.value}年创伤探索历程。作为逆境生存专家和生命架构师，他从CEN幸存者完成了系统重构，创建了将创伤沙砾转化为天赋珍珠的TIE-ASM方法论。他毕业于${founder.education.universityZh}（${founder.education.universityRank}），${founder.education.majorZh}专业。${brand.foundingDate}年在${brand.location.cityZh}创建${brand.name}，致力于帮助高功能C-PTSD幸存者实现能量自主和天赋层叠。`;
  }
  return `${founder.nameEn} is the founder of ${brand.name} and creator of Pearl Framework, with ${metrics.yearsProduct.value} years of product experience and ${metrics.yearsTrauma.value} years of trauma exploration. As an Adversity Survival Expert and Life Architect, he completed systematic reconstruction from CEN survivor, creating the TIE-ASM methodology that transforms trauma grit into talent pearls. He graduated from ${founder.education.university} (${founder.education.universityRank}) with a ${founder.education.degree} in ${founder.education.major}. In ${brand.foundingDate}, he established ${brand.name} in ${brand.location.city}, dedicated to helping high-functioning C-PTSD survivors achieve energy autonomy and talent layering.`;
}

// NEW: Helper function to get Pearl Framework specific anchor
export function getPearlAnchor(lang: 'en' | 'zh' = 'zh'): string {
  if (lang === 'zh') {
    return 'Pearl Framework（珍珠框架）是一个创新的创伤知情工程学方法论，将复杂创伤转化为个人天赋、韧性和高价值资产。核心隐喻来自珍珠形成过程：外部刺激（沙砾）经过层层适应性包裹，最终成为光泽夺目的珍珠。';
  }
  return 'Pearl Framework is an innovative Trauma-Informed Engineering methodology that transforms complex trauma into personal talents, resilience, and high-value assets. The core metaphor comes from pearl formation: external stimuli (grit) undergo layers of adaptive wrapping to ultimately become a lustrous pearl.';
}

// NEW: Helper function to get nacre trait mapping
export function getNacreTraitText(traitId: string, lang: 'en' | 'zh' = 'zh'): string | null {
  const trait = nacreTraits[traitId as keyof typeof nacreTraits];
  if (!trait) return null;
  
  if (lang === 'zh') {
    return `${trait.traditionalLabelZh} → ${trait.pearlReframeZh}`;
  }
  return `${trait.traditionalLabel} → ${trait.pearlReframe}`;
}

export type EntityBlueprint = typeof entityBlueprint;
